/**
 * Competition maths — fixtures, tables, group draws, brackets, simulated
 * results. (§9.1)
 *
 * ── Why this is a separate, DOM-free module ────────────────────────────────
 *
 * Career and World Cup are the first parts of the front end with real state
 * that must be CORRECT: a league table that mis-sorts, a fixture list that
 * gives a team two home games in a row against the same side, or a bracket that
 * advances the loser are bugs a player notices immediately and an agent cannot
 * see from a screenshot. So all of it is pure functions over plain data, with
 * no DOM, no storage and no clock — unit-tested in node (competition.test.ts).
 *
 * Nothing here knows what a team IS. Ratings arrive through a `ratingOf`
 * callback so this module never imports teams.ts, which keeps the dependency
 * arrow pointing one way (teams → competition) and lets the tests drive it with
 * three fake teams instead of a 16-club league.
 *
 * ⚠ This is NOT simulation code in the §15 sense. It never runs inside a tick,
 * never touches WorldState and never crosses the wire, so `Math.*` and a plain
 * mulberry32 are fine here — §15.3's PCG32 rule is about client/server
 * agreement inside `step()`. Match RESULTS still come from the real sim when
 * the player actually plays; this only fills in the fixtures they didn't.
 */

// ---------------------------------------------------------------------------
// Seeded randomness
// ---------------------------------------------------------------------------

/** mulberry32 — 32-bit state, uniform enough for a fixture simulator, and
 *  identical on every engine so a stored seed reproduces a stored season. */
export function createRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** FNV-1a. Turns a string key (team id, "season-3-round-7") into a seed. */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

// ---------------------------------------------------------------------------
// Fixtures
// ---------------------------------------------------------------------------

export interface Fixture {
  /** 0-based matchday. */
  round: number;
  home: string;
  away: string;
}

export interface MatchResult {
  round: number;
  home: string;
  away: string;
  homeGoals: number;
  awayGoals: number;
  /** True when the human played it rather than the simulator. */
  played: boolean;
}

/**
 * Single round-robin by the circle method: team 0 is pinned, everyone else
 * rotates one seat per round, so every team meets every other exactly once in
 * n-1 rounds with n/2 games per round.
 *
 * The venue rule matters more than it looks. Naively making `seats[i]` always
 * the home side leaves the pinned team home in all 15 rounds; the obvious fix
 * of flipping on `(r + i) % 2` is WORSE, not better — because a team's seat
 * index moves with the rotation, that flip measured 0 and 14 home games out of
 * 15 across a 16-team league (one club never played at home all season). The
 * rule below — alternate the PINNED pairing by round, decide every other
 * pairing by seat parity — measures 7–8 home games for every team with at most
 * two consecutive same-venue fixtures. Do not "simplify" it back.
 *
 * Requires an even team count — every competition in this game has one (16
 * clubs, 4-team groups), and an odd list would need byes, which the season
 * model has no concept of. An odd list drops its last team rather than
 * producing a silently broken schedule.
 */
export function roundRobin(teamIds: readonly string[]): Fixture[] {
  const ids = teamIds.slice(0, teamIds.length - (teamIds.length % 2));
  const n = ids.length;
  if (n < 2) return [];
  const half = n / 2;
  const rot = ids.slice(1);
  const out: Fixture[] = [];
  for (let r = 0; r < n - 1; r++) {
    const seats = [ids[0]!, ...rot];
    for (let i = 0; i < half; i++) {
      const a = seats[i]!;
      const b = seats[n - 1 - i]!;
      const aIsHome = i === 0 ? r % 2 === 0 : i % 2 === 0;
      out.push({ round: r, home: aIsHome ? a : b, away: aIsHome ? b : a });
    }
    rot.unshift(rot.pop()!);
  }
  return out;
}

/**
 * A full league season. `legs: 2` appends the reverse fixtures with the venues
 * swapped — the standard home-and-away format, and the reason a 16-club league
 * runs 30 matchdays.
 */
export function generateFixtures(teamIds: readonly string[], legs: 1 | 2 = 2): Fixture[] {
  const first = roundRobin(teamIds);
  if (legs === 1) return first;
  const roundsPerLeg = teamIds.length - 1;
  const second = first.map((f) => ({
    round: f.round + roundsPerLeg,
    home: f.away,
    away: f.home,
  }));
  return [...first, ...second];
}

/** The fixtures of one matchday. */
export function fixturesInRound(fixtures: readonly Fixture[], round: number): Fixture[] {
  return fixtures.filter((f) => f.round === round);
}

/** The one fixture in `round` involving `teamId`, or null (byes/elimination). */
export function fixtureFor(
  fixtures: readonly Fixture[],
  round: number,
  teamId: string,
): Fixture | null {
  return fixtures.find((f) => f.round === round && (f.home === teamId || f.away === teamId)) ?? null;
}

// ---------------------------------------------------------------------------
// League table
// ---------------------------------------------------------------------------

export interface TableRow {
  teamId: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
  /** Most recent results first, newest ≤ 5, as 'W' | 'D' | 'L'. */
  form: string[];
}

const WIN_POINTS = 3;
const DRAW_POINTS = 1;
const FORM_LENGTH = 5;

/**
 * Standings from a result list. Sorted points → goal difference → goals for →
 * team id; the final id tiebreak is not football's rule (that would be
 * head-to-head or an alphabetical draw) but it IS deterministic, which is what
 * stops two equal teams swapping places on every repaint.
 */
export function computeTable(
  teamIds: readonly string[],
  results: readonly MatchResult[],
): TableRow[] {
  const rows = new Map<string, TableRow>();
  for (const id of teamIds) {
    rows.set(id, {
      teamId: id, played: 0, won: 0, drawn: 0, lost: 0,
      goalsFor: 0, goalsAgainst: 0, goalDifference: 0, points: 0, form: [],
    });
  }
  for (const r of results) {
    const home = rows.get(r.home);
    const away = rows.get(r.away);
    if (!home || !away) continue; // a result for a team not in this competition
    home.played++; away.played++;
    home.goalsFor += r.homeGoals; home.goalsAgainst += r.awayGoals;
    away.goalsFor += r.awayGoals; away.goalsAgainst += r.homeGoals;
    if (r.homeGoals > r.awayGoals) {
      home.won++; away.lost++; home.points += WIN_POINTS;
      home.form.unshift('W'); away.form.unshift('L');
    } else if (r.homeGoals < r.awayGoals) {
      away.won++; home.lost++; away.points += WIN_POINTS;
      away.form.unshift('W'); home.form.unshift('L');
    } else {
      home.drawn++; away.drawn++;
      home.points += DRAW_POINTS; away.points += DRAW_POINTS;
      home.form.unshift('D'); away.form.unshift('D');
    }
  }
  const out = [...rows.values()];
  for (const row of out) {
    row.goalDifference = row.goalsFor - row.goalsAgainst;
    row.form.length = Math.min(row.form.length, FORM_LENGTH);
  }
  out.sort(
    (a, b) =>
      b.points - a.points ||
      b.goalDifference - a.goalDifference ||
      b.goalsFor - a.goalsFor ||
      (a.teamId < b.teamId ? -1 : a.teamId > b.teamId ? 1 : 0),
  );
  return out;
}

/** 1-based league position, or 0 if the team is not in the table. */
export function positionInTable(table: readonly TableRow[], teamId: string): number {
  const at = table.findIndex((r) => r.teamId === teamId);
  return at < 0 ? 0 : at + 1;
}

// ---------------------------------------------------------------------------
// Result simulation
// ---------------------------------------------------------------------------

/** Goals per team in an even game — a real league averages ~2.7 total. */
const BASE_GOALS = 1.35;
/** Playing at home is worth roughly a quarter of a goal. */
const HOME_ADVANTAGE = 0.22;
/** Each rating point of superiority buys this many expected goals. */
const RATING_TO_GOALS = 0.035;
/** Knuth's Poisson sampler is unbounded; a hostile rng must not hang the UI. */
const MAX_GOALS = 12;

function poisson(mu: number, rng: () => number): number {
  const limit = Math.exp(-mu);
  let k = 0;
  let p = 1;
  do {
    k++;
    p *= rng();
  } while (p > limit && k <= MAX_GOALS);
  return k - 1;
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * A plausible scoreline for a fixture nobody played. Independent Poisson draws
 * around a rating-and-venue-adjusted expectation — the standard football model,
 * and enough to make an 86-rated side finish above a 70-rated one over 30 games
 * without making any single result a foregone conclusion.
 */
export function simulateResult(
  homeRating: number,
  awayRating: number,
  rng: () => number,
): { homeGoals: number; awayGoals: number } {
  const edge = (homeRating - awayRating) * RATING_TO_GOALS;
  const muHome = clamp(BASE_GOALS + HOME_ADVANTAGE + edge, 0.25, 4.5);
  const muAway = clamp(BASE_GOALS - HOME_ADVANTAGE - edge, 0.2, 4.5);
  return { homeGoals: poisson(muHome, rng), awayGoals: poisson(muAway, rng) };
}

/** Simulate every fixture of a matchday except the ones already in `skip`. */
export function simulateRound(
  fixtures: readonly Fixture[],
  round: number,
  ratingOf: (teamId: string) => number,
  rng: () => number,
  skip: (f: Fixture) => boolean = () => false,
): MatchResult[] {
  const out: MatchResult[] = [];
  for (const f of fixturesInRound(fixtures, round)) {
    if (skip(f)) continue;
    const { homeGoals, awayGoals } = simulateResult(ratingOf(f.home), ratingOf(f.away), rng);
    out.push({ round, home: f.home, away: f.away, homeGoals, awayGoals, played: false });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Group stage
// ---------------------------------------------------------------------------

export interface Group {
  /** 'A'…'H'. */
  name: string;
  teamIds: string[];
}

const GROUP_LETTERS = 'ABCDEFGHIJKLMNOP';

/**
 * A pot-based draw: teams are ranked, split into `groupCount` pots of equal
 * size, each pot is shuffled, then dealt one team per group. That is how a real
 * draw works and it matters — dealing a shuffled flat list produces a "group of
 * death" and a group of four minnows about as often as not, which makes the
 * tournament read as broken even though it is only unlucky.
 */
export function drawGroups(
  rankedTeamIds: readonly string[],
  groupCount: number,
  rng: () => number,
): Group[] {
  const groups: Group[] = Array.from({ length: groupCount }, (_, i) => ({
    name: GROUP_LETTERS[i] ?? String(i + 1),
    teamIds: [],
  }));
  const perPot = Math.floor(rankedTeamIds.length / groupCount);
  for (let pot = 0; pot < perPot; pot++) {
    const slice = shuffle(rankedTeamIds.slice(pot * groupCount, (pot + 1) * groupCount), rng);
    slice.forEach((id, i) => groups[i]?.teamIds.push(id));
  }
  return groups;
}

/** Fisher–Yates with the supplied rng. Returns a new array. */
export function shuffle<T>(items: readonly T[], rng: () => number): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const a = out[i]!;
    out[i] = out[j]!;
    out[j] = a;
  }
  return out;
}

/**
 * Every group's fixtures in one flat list, with a GLOBAL round number so all
 * eight groups play their matchday 1 together — which is both how a real
 * tournament is scheduled and what lets the store advance the whole stage in
 * one "play/sim the next round" step.
 */
export function groupStageFixtures(groups: readonly Group[]): Fixture[] {
  const out: Fixture[] = [];
  for (const g of groups) out.push(...roundRobin(g.teamIds));
  return out;
}

/** Matchdays in the group stage — 3 for the standard four-team group. */
export function groupRoundCount(groups: readonly Group[]): number {
  const size = groups[0]?.teamIds.length ?? 0;
  return Math.max(0, size - 1);
}

/**
 * The 16 qualifiers, ordered so `buildBracket` pairs them the way a real draw
 * does: winners cross with the runners-up of the NEIGHBOURING group (A1–B2,
 * B1–A2, …). Pairing A1–A2 would put group-mates back together immediately,
 * and pairing all winners together would end the tournament in the first round.
 */
export function qualifiersFromGroups(
  groups: readonly Group[],
  results: readonly MatchResult[],
): string[] {
  const standings = groups.map((g) => computeTable(g.teamIds, results));
  const first = (i: number): string => standings[i]?.[0]?.teamId ?? '';
  const second = (i: number): string => standings[i]?.[1]?.teamId ?? '';
  const out: string[] = [];
  for (let i = 0; i + 1 < groups.length; i += 2) out.push(first(i), second(i + 1));
  for (let i = 0; i + 1 < groups.length; i += 2) out.push(first(i + 1), second(i));
  return out;
}

// ---------------------------------------------------------------------------
// Knockout bracket
// ---------------------------------------------------------------------------

export interface BracketMatch {
  /** `r{round}s{slot}` — stable, so a persisted bracket can be addressed. */
  id: string;
  /** 0 = first knockout round; the last round is the final. */
  round: number;
  slot: number;
  home: string | null;
  away: string | null;
  homeGoals: number | null;
  awayGoals: number | null;
  /** [home, away] shootout score when the tie finished level; null otherwise. */
  shootout: [number, number] | null;
  winner: string | null;
}

const KNOCKOUT_NAMES: Record<number, string> = {
  1: 'FINAL',
  2: 'SEMI-FINAL',
  4: 'QUARTER-FINAL',
  8: 'ROUND OF 16',
  16: 'ROUND OF 32',
};

/** Display name for a round, derived from how many ties it contains. */
export function roundName(matchesInRound: number): string {
  return KNOCKOUT_NAMES[matchesInRound] ?? `ROUND OF ${matchesInRound * 2}`;
}

/**
 * An empty bracket seeded with the qualifiers. Only round 0 has teams; every
 * later round is filled in by `setBracketResult` as winners come through, which
 * is what makes the bracket screen legible while it is still half-empty.
 */
export function buildBracket(qualifiers: readonly string[]): BracketMatch[] {
  const n = qualifiers.length;
  const out: BracketMatch[] = [];
  if (n < 2) return out;
  const rounds = Math.ceil(Math.log2(n));
  for (let round = 0; round < rounds; round++) {
    const count = Math.max(1, Math.floor(n / Math.pow(2, round + 1)));
    for (let slot = 0; slot < count; slot++) {
      out.push({
        id: `r${round}s${slot}`,
        round,
        slot,
        home: round === 0 ? (qualifiers[slot * 2] ?? null) : null,
        away: round === 0 ? (qualifiers[slot * 2 + 1] ?? null) : null,
        homeGoals: null,
        awayGoals: null,
        shootout: null,
        winner: null,
      });
    }
  }
  return out;
}

export function bracketRound(bracket: readonly BracketMatch[], round: number): BracketMatch[] {
  return bracket.filter((m) => m.round === round);
}

export function bracketRoundCount(bracket: readonly BracketMatch[]): number {
  return bracket.reduce((max, m) => Math.max(max, m.round + 1), 0);
}

/** The tie `teamId` is in for `round`, if any (they may be eliminated). */
export function bracketMatchFor(
  bracket: readonly BracketMatch[],
  round: number,
  teamId: string,
): BracketMatch | null {
  return bracket.find((m) => m.round === round && (m.home === teamId || m.away === teamId)) ?? null;
}

/**
 * Record a knockout result and push the winner into the next round.
 *
 * A knockout tie cannot be drawn, so a level score MUST be resolved: pass a
 * `shootout` pair, or let this pick one from `rng` (the loser of the coin-flip
 * gets 4, the winner 5 — a plausible-looking shootout rather than a fake extra
 * goal, so the printed scoreline stays the one the player actually played).
 *
 * Returns a NEW array: the store keeps state immutable so a failed save never
 * leaves a half-mutated bracket in memory.
 */
export function setBracketResult(
  bracket: readonly BracketMatch[],
  matchId: string,
  homeGoals: number,
  awayGoals: number,
  rng: () => number,
): BracketMatch[] {
  const next = bracket.map((m) => ({ ...m }));
  const match = next.find((m) => m.id === matchId);
  if (!match || !match.home || !match.away) return next;
  match.homeGoals = homeGoals;
  match.awayGoals = awayGoals;
  if (homeGoals > awayGoals) {
    match.winner = match.home;
  } else if (awayGoals > homeGoals) {
    match.winner = match.away;
  } else {
    const homeWins = rng() < 0.5;
    match.shootout = homeWins ? [5, 4] : [4, 5];
    match.winner = homeWins ? match.home : match.away;
  }
  // Propagate: two ties feed one tie in the next round, the even slot arriving
  // as home. Losing this rule is how a bracket quietly advances the wrong side.
  const parent = next.find((m) => m.round === match.round + 1 && m.slot === Math.floor(match.slot / 2));
  if (parent) {
    if (match.slot % 2 === 0) parent.home = match.winner;
    else parent.away = match.winner;
  }
  return next;
}

/** simulateResult always gives the "home" side the venue bonus; cancel it out
 *  by handing the away side the same edge in rating terms. A World Cup knockout
 *  is played on neutral ground, so neither bracket side should get it. */
const HOME_ADVANTAGE_OFF = -HOME_ADVANTAGE / RATING_TO_GOALS;

/** Simulate every unplayed tie of a round (skipping `skipId`, the human's). */
export function simulateBracketRound(
  bracket: readonly BracketMatch[],
  round: number,
  ratingOf: (teamId: string) => number,
  rng: () => number,
  skipId: string | null = null,
): BracketMatch[] {
  let next = bracket.map((m) => ({ ...m }));
  for (const m of bracketRound(next, round)) {
    if (m.id === skipId || m.winner !== null || !m.home || !m.away) continue;
    // No home advantage on neutral ground — a World Cup knockout has no host.
    const { homeGoals, awayGoals } = simulateResult(
      ratingOf(m.home) + HOME_ADVANTAGE_OFF,
      ratingOf(m.away),
      rng,
    );
    next = setBracketResult(next, m.id, homeGoals, awayGoals, rng);
  }
  return next;
}

/** True once every tie in the last round has a winner. */
export function bracketComplete(bracket: readonly BracketMatch[]): boolean {
  const last = bracketRoundCount(bracket) - 1;
  if (last < 0) return false;
  return bracketRound(bracket, last).every((m) => m.winner !== null);
}

export function bracketChampion(bracket: readonly BracketMatch[]): string | null {
  const last = bracketRoundCount(bracket) - 1;
  if (last < 0) return null;
  return bracketRound(bracket, last)[0]?.winner ?? null;
}
